import { localeTag } from '@/i18n/format';
import { useTranslation } from 'react-i18next';
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Hexagon, Loader2, ChevronDown, ChevronRight, BarChart2,
  X, Minus, Maximize2, Minimize2, FileDown, LayoutGrid, Edit3,
} from 'lucide-react';
import { useColumnFilters, FilterHeaderCell } from '../common/ColumnFilter';
import {
  DraggableCard, SelectableTable, ChartTypeModal, BIChart,
  type CardPosition, type ChartConfig, type SelectionState,
} from '../Info/BIComponents';
import { useMapStore } from '../../store/map.store';
import { captureMap, captureDashboard } from '../../utils/mapCapture';
import { MiniPDF } from '../../utils/miniPdf';
import { cn } from '../../utils/cn';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import { GeoJSON } from 'ol/format';
import TileWMS from 'ol/source/TileWMS';
import ImageWMS from 'ol/source/ImageWMS';

/* ── Types ── */

interface WfsFeature {
  id: string;
  properties: Record<string, any>;
  geometryType?: string;
}

interface LayerResult {
  layerName: string;
  features: WfsFeature[];
  keys: string[];
  numericKeys: string[];
  stats?: StatsRow[];
}

interface StatsRow {
  _stat: string;
  [key: string]: string | number;
}

interface AreaAnalysisPanelProps {
  onClose: () => void;
  drawnFeature: Feature<Geometry> | null;
}

const EXCLUDED_KEYS = new Set([
  'geometry', 'geom', 'the_geom', 'wkb_geometry', 'bbox',
  'hashKey', 'featureId', 'ogc_fid', 'gid',
]);

/* ── Helpers ── */

function parseNumeric(raw: any): number | null {
  if (raw == null || typeof raw === 'boolean') return null;
  const s = String(raw).replace(/[%,\s]/g, '').trim();
  const n = Number(s);
  return isNaN(n) ? null : n;
}

/** Compute sum, avg, max, min for all numeric columns regardless of geometry type */
function computeStats(features: WfsFeature[], keys: string[]): { numericKeys: string[]; stats: StatsRow[] } {
  const numericKeys = keys.filter((k) =>
    features.some((f) => parseNumeric(f.properties[k]) !== null),
  );

  if (numericKeys.length === 0) return { numericKeys: [], stats: [] };

  const sumRow: StatsRow = { _stat: 'SUM' };
  const avgRow: StatsRow = { _stat: 'AVG' };
  const maxRow: StatsRow = { _stat: 'MAX' };
  const minRow: StatsRow = { _stat: 'MIN' };

  for (const key of numericKeys) {
    let sum = 0;
    let count = 0;
    let max = -Infinity;
    let min = Infinity;

    for (const f of features) {
      const v = parseNumeric(f.properties[key]);
      if (v !== null) {
        sum += v;
        count++;
        if (v > max) max = v;
        if (v < min) min = v;
      }
    }

    if (count > 0) {
      sumRow[key] = Math.round(sum * 100) / 100;
      avgRow[key] = Math.round((sum / count) * 100) / 100;
      maxRow[key] = Math.round(max * 100) / 100;
      minRow[key] = Math.round(min * 100) / 100;
    } else {
      sumRow[key] = '—';
      avgRow[key] = '—';
      maxRow[key] = '—';
      minRow[key] = '—';
    }
  }

  return { numericKeys, stats: [sumRow, avgRow, maxRow, minRow] };
}

const PAGE_SIZE = 50;

/* ── Expanded Layer Table (owns its own filter + pagination state) ── */

const ExpandedLayerTable: React.FC<{ lr: LayerResult }> = ({ lr }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);

  // Features with .properties for filtering
  const featuresForFilter = useMemo(
    () => lr.features.map((f) => f.properties),
    [lr.features],
  );

  const { filters, setFilter, hasActiveFilters, clearFilters, filteredFeatures, stringColumns } =
    useColumnFilters(featuresForFilter, lr.keys);

  // Stats are always on FULL (unfiltered) data
  const stats = lr.stats;

  const totalPages = Math.ceil(filteredFeatures.length / PAGE_SIZE);
  const pageFeatures = filteredFeatures.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [filters]);

  return (
    <>
      {/* Scrollable table area */}
      <div className="flex-1 min-h-0 overflow-auto">
        <table className="w-full text-[10px]">
          <thead className="sticky top-0 z-10">
            <tr>
              {lr.keys.map((k) => (
                <FilterHeaderCell
                  key={k}
                  columnName={k}
                  isFilterable={stringColumns.has(k)}
                  filterValue={filters[k] || ''}
                  onFilterChange={setFilter}
                  className={cn(
                    'px-2 py-1.5 text-left font-medium whitespace-nowrap border-b border-gn-light bg-gn-secondary',
                    lr.numericKeys.includes(k) ? 'text-gn-blue' : 'text-gn-secondary',
                  )}
                >
                  {k}
                </FilterHeaderCell>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageFeatures.map((props, idx) => (
              <tr key={idx}
                className={cn('border-b border-gn-light', idx % 2 === 0 ? '' : 'bg-gn-tertiary')}
              >
                {lr.keys.map((k) => (
                  <td key={k} className="px-2 py-1 text-gn-primary whitespace-nowrap max-w-[150px] truncate">
                    {props[k] != null ? String(props[k]) : '—'}
                  </td>
                ))}
              </tr>
            ))}

            {/* Statistics rows — always on full data */}
            {stats && stats.map((stat) => (
              <tr key={stat._stat} className={cn(
                'border-b border-gn-light',
                stat._stat === 'SUM' ? 'bg-gn-blue-light' :
                stat._stat === 'AVG' ? 'bg-gn-success-light' :
                stat._stat === 'MAX' ? 'bg-gn-warning-light' :
                'bg-gn-accent-light',
              )}>
                {lr.keys.map((k, ki) => {
                  if (ki === 0) {
                    return (
                      <td key={k} className="px-2 py-1.5 font-bold text-[10px] whitespace-nowrap">
                        <span className={cn(
                          stat._stat === 'SUM' ? 'text-gn-blue' :
                          stat._stat === 'AVG' ? 'text-gn-success' :
                          stat._stat === 'MAX' ? 'text-gn-warning' :
                          'text-gn-accent',
                        )}>
                          {t(`stats.${String(stat._stat).toLowerCase()}`, { defaultValue: String(stat._stat) })}
                        </span>
                      </td>
                    );
                  }
                  const val = stat[k];
                  return (
                    <td key={k} className={cn(
                      'px-2 py-1.5 font-semibold text-[10px] whitespace-nowrap',
                      val != null && val !== '—' ? 'text-gn-primary' : 'text-gn-muted',
                    )}>
                      {val != null ? (typeof val === 'number' ? val.toLocaleString(localeTag()) : String(val)) : ''}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer: filter info + pagination */}
      <div className="shrink-0 flex items-center justify-between px-3 py-1 border-t border-gn-light bg-gn-secondary">
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-[9px] text-gn-warning hover:text-gn-warning flex items-center gap-0.5">
              <X size={9} /> {t('table.clear_filters')}
            </button>
          )}
          <span className="text-[9px] text-gn-secondary">
            {hasActiveFilters
              ? t('table.rows_of', { shown: filteredFeatures.length, total: lr.features.length })
              : t('table.rows', { count: lr.features.length })}
          </span>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page <= 0}
              className="text-[10px] text-gn-blue disabled:text-gn-muted"
            >
              {t('table.prev')}
            </button>
            <span className="text-[10px] text-gn-secondary">{page + 1} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="text-[10px] text-gn-blue disabled:text-gn-muted"
            >
              {t('table.next')}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

/* ══════════════════════════════════════════════════════
   Bottom-anchored drag/resize hook (matches CatchmentResultPanel)
   ══════════════════════════════════════════════════════ */

function useBottomDragResize(
  initialRect: { left: number; bottom: number; w: number; h: number },
  minSize = { w: 500, h: 200 },
) {
  const [rect, setRect] = useState(initialRect);
  const dragRef = useRef<{ startX: number; startY: number; origLeft: number; origBottom: number } | null>(null);
  const resizeRef = useRef<{ startX: number; startY: number; origW: number; origH: number; edge: string } | null>(null);

  const onDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, origLeft: rect.left, origBottom: rect.bottom };
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      setRect((prev) => ({
        ...prev,
        left: dragRef.current!.origLeft + ev.clientX - dragRef.current!.startX,
        bottom: dragRef.current!.origBottom - (ev.clientY - dragRef.current!.startY),
      }));
    };
    const onUp = () => { dragRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [rect.left, rect.bottom]);

  const onResizeStart = useCallback((edge: string) => (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    resizeRef.current = { startX: e.clientX, startY: e.clientY, origW: rect.w, origH: rect.h, edge };
    const origBottom = rect.bottom;
    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return;
      const dx = ev.clientX - resizeRef.current.startX;
      const dy = ev.clientY - resizeRef.current.startY;
      setRect((prev) => {
        const newRect = { ...prev };
        if (resizeRef.current!.edge.includes('right')) newRect.w = Math.max(minSize.w, resizeRef.current!.origW + dx);
        if (resizeRef.current!.edge.includes('left')) {
          const newW = Math.max(minSize.w, resizeRef.current!.origW - dx);
          newRect.left = prev.left + (prev.w - newW);
          newRect.w = newW;
        }
        if (resizeRef.current!.edge.includes('top')) newRect.h = Math.max(minSize.h, resizeRef.current!.origH - dy);
        if (resizeRef.current!.edge.includes('bottom')) {
          const newH = Math.max(minSize.h, resizeRef.current!.origH + dy);
          newRect.h = newH;
          newRect.bottom = origBottom - (newH - resizeRef.current!.origH);
        }
        return newRect;
      });
    };
    const onUp = () => { resizeRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [rect.w, rect.h, rect.left, rect.bottom, minSize.w, minSize.h]);

  return { rect, setRect, onDragStart, onResizeStart };
}

/* ══════════════════════════════════════════════════════
   Component
   ══════════════════════════════════════════════════════ */

export const AreaAnalysisPanel: React.FC<AreaAnalysisPanelProps> = ({
  onClose,
  drawnFeature,
}) => {
  const { t } = useTranslation();
  const [results, setResults] = useState<LayerResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedLayer, setExpandedLayer] = useState<string | null>(null);
  const [minimized, setMinimized] = useState(false);

  /* ── BI / Fullscreen state ── */
  const [fullscreen, setFullscreen] = useState(false);
  const biContainerRef = useRef<HTMLDivElement | null>(null);
  const [cardPositions, setCardPositions] = useState<CardPosition[]>([]);
  const [charts, setCharts] = useState<ChartConfig[]>([]);
  const [chartPositions, setChartPositions] = useState<CardPosition[]>([]);
  const [selection, setSelection] = useState<SelectionState | null>(null);
  const [showChartModal, setShowChartModal] = useState(false);
  const [editingChartId, setEditingChartId] = useState<string | null>(null);
  const [reportName, setReportName] = useState(() => t('area.report_name'));
  const [editingReportName, setEditingReportName] = useState(false);
  const [exporting, setExporting] = useState(false);
  const mapInstance = useMapStore((s) => s.mapInstance);

  const initialW = Math.min(window.innerWidth * 0.85, 1100);
  const initialH = Math.min(window.innerHeight * 0.4, 420);
  const { rect, onDragStart, onResizeStart } = useBottomDragResize({
    left: Math.max(8, (window.innerWidth - initialW) / 2),
    bottom: 8,
    w: initialW,
    h: initialH,
  });

  // Refs for latest state (avoids stale closures in drag handlers)
  const cardPosRef = useRef(cardPositions);
  cardPosRef.current = cardPositions;
  const chartPosRef = useRef(chartPositions);
  chartPosRef.current = chartPositions;

  /** Auto-query when a polygon is drawn */
  useEffect(() => {
    if (drawnFeature) runAnalysis(drawnFeature);
  }, [drawnFeature]);

  const runAnalysis = useCallback(async (feature: Feature<Geometry>) => {
    const mapInst = useMapStore.getState().mapInstance;
    if (!mapInst) return;

    setLoading(true);
    setResults([]);

    try {
      const geoJson = new GeoJSON();
      const featureObj = geoJson.writeFeatureObject(feature, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      });
      const geom = featureObj.geometry;
      if (!geom) return;

      const coords = (geom as any).coordinates?.[0] as number[][] | undefined;
      if (!coords) return;
      let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
      coords.forEach(([lon, lat]) => {
        if (lon < minLon) minLon = lon;
        if (lat < minLat) minLat = lat;
        if (lon > maxLon) maxLon = lon;
        if (lat > maxLat) maxLat = lat;
      });

      const allResults: LayerResult[] = [];
      const promises: Promise<void>[] = [];

      mapInst.getLayers().forEach((layer: any) => {
        if (!layer.getVisible()) return;
        const wmsSource = layer.getSource?.();
        if (!wmsSource || (!(wmsSource instanceof TileWMS) && !(wmsSource instanceof ImageWMS))) return;
        const layerName = layer.get('name');
        if (!layerName || layerName.toLowerCase().includes('label')) return;

        const wmsUrl: string = (wmsSource as any).getUrls?.()[0] || (wmsSource as any).getUrl?.() || '';
        if (!wmsUrl) return;
        const wfsUrl = wmsUrl.replace(/\/wms\/?$/i, '/ows').replace(/\/wms\?/i, '/ows?');

        // Get actual WMS LAYERS param (includes workspace prefix)
        const wmsParams = wmsSource.getParams?.() || {};
        const typeName: string = wmsParams.LAYERS || layerName;

                const bboxStr = `${minLon.toFixed(6)},${minLat.toFixed(6)},${maxLon.toFixed(6)},${maxLat.toFixed(6)},EPSG:4326`;

        const params = new URLSearchParams({
          service: 'WFS',
          version: '1.1.0',
          request: 'GetFeature',
          typeName,
          // EPSG:4326 (not the urn form): GeoServer then reads the box and writes the GeoJSON as lon,lat.
        srsName: 'EPSG:4326',
          outputFormat: 'application/json',
          bbox: bboxStr,
          maxFeatures: '50000',
        });

        const promise = fetch(`${wfsUrl}?${params.toString()}`)
          .then((r) => r.json())
          .then((data) => {
            if (data?.features?.length > 0) {
              const keys = Object.keys(data.features[0].properties || {}).filter(
                (k) => !EXCLUDED_KEYS.has(k.toLowerCase()),
              );

              const features: WfsFeature[] = data.features.map((f: any) => ({
                id: f.id || '',
                properties: f.properties || {},
                geometryType: f.geometry?.type || '',
              }));

              // Compute stats for ALL layers (regardless of geometry type)
              const { numericKeys, stats } = computeStats(features, keys);

              allResults.push({ layerName, features, keys, numericKeys, stats });
            }
          })
          .catch((err) => {
            console.warn(`WFS query failed for ${layerName}:`, err);
          });

        promises.push(promise);
      });

      await Promise.all(promises);

      // Sort: layers with stats first, then by feature count
      allResults.sort((a, b) => {
        if (a.stats?.length && !b.stats?.length) return -1;
        if (!a.stats?.length && b.stats?.length) return 1;
        return b.features.length - a.features.length;
      });

      setResults(allResults);
      if (allResults.length > 0) setExpandedLayer(allResults[0].layerName);
    } catch (err: any) {
      console.error('Area analysis failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /* ── BI tables for fullscreen mode ── */
  const allTables = useMemo(() => {
    return results.map((lr) => ({
      id: `area-${lr.layerName}`,
      layerName: lr.layerName,
      keys: lr.keys,
      features: lr.features.map((f) => f.properties),
      numericKeys: lr.numericKeys,
      stats: lr.stats,
    }));
  }, [results]);

  /* ── Collision resolution ── */
  function resolveAllCollisions(positions: CardPosition[]): CardPosition[] {
    const all = positions.map((p) => ({ ...p }));
    const gap = 8;
    let changed = true;
    let iterations = 0;
    while (changed && iterations < 100) {
      changed = false;
      iterations++;
      all.sort((a, b) => a.y - b.y || a.x - b.x);
      for (let i = 0; i < all.length; i++) {
        for (let j = i + 1; j < all.length; j++) {
          const a = all[i], b = all[j];
          if (a.x < b.x + b.w + gap && a.x + a.w + gap > b.x &&
              a.y < b.y + b.h + gap && a.y + a.h + gap > b.y) {
            b.y = a.y + a.h + gap;
            changed = true;
          }
        }
      }
    }
    return all;
  }

  function applyCollisions(tables: CardPosition[], chs: CardPosition[]) {
    const tableIds = new Set(tables.map((p) => p.id));
    const resolved = resolveAllCollisions([...tables, ...chs]);
    return {
      tables: resolved.filter((p) => tableIds.has(p.id)),
      charts: resolved.filter((p) => !tableIds.has(p.id)),
    };
  }

  const enterFullscreen = useCallback(() => {
    setFullscreen(true);
    const cols = Math.min(3, allTables.length);
    const cardW = 420, cardH = 300, gap = 12;
    const positions: CardPosition[] = allTables.map((t, i) => ({
      id: t.id,
      x: (i % cols) * (cardW + gap) + gap,
      y: Math.floor(i / cols) * (cardH + gap) + gap,
      w: cardW,
      h: cardH,
    }));
    setCardPositions(positions);
  }, [allTables]);

  const autoArrange = useCallback(() => {
    const allCards = [...cardPosRef.current, ...chartPosRef.current];
    if (allCards.length === 0) return;
    const cols = Math.min(3, allCards.length);
    const gap = 12;
    const tableIds = new Set(cardPosRef.current.map((p) => p.id));
    const sorted = [...allCards].sort((a, b) => {
      const aT = tableIds.has(a.id) ? 0 : 1;
      const bT = tableIds.has(b.id) ? 0 : 1;
      return aT - bT;
    });
    let y = gap, rowH = 0, col = 0;
    sorted.forEach((card) => {
      card.x = col * (card.w + gap) + gap;
      card.y = y;
      rowH = Math.max(rowH, card.h);
      col++;
      if (col >= cols) { col = 0; y += rowH + gap; rowH = 0; }
    });
    setCardPositions(sorted.filter((p) => tableIds.has(p.id)));
    setChartPositions(sorted.filter((p) => !tableIds.has(p.id)));
  }, []);

  const moveCard = useCallback((id: string, x: number, y: number) => {
    const tables = cardPosRef.current.map((p) => p.id === id ? { ...p, x, y } : { ...p });
    const chs = chartPosRef.current.map((p) => p.id === id ? { ...p, x, y } : { ...p });
    const resolved = applyCollisions(tables, chs);
    setCardPositions(resolved.tables);
    setChartPositions(resolved.charts);
  }, []);

  const resizeCard = useCallback((id: string, w: number, h: number) => {
    const tables = cardPosRef.current.map((p) => p.id === id ? { ...p, w, h } : { ...p });
    const chs = chartPosRef.current.map((p) => p.id === id ? { ...p, w, h } : { ...p });
    const resolved = applyCollisions(tables, chs);
    setCardPositions(resolved.tables);
    setChartPositions(resolved.charts);
  }, []);

  /* ── Chart management ── */
  const handleChartConfirm = useCallback((config: Omit<ChartConfig, 'id'>) => {
    if (editingChartId) {
      setCharts((prev) => prev.map((c) => c.id === editingChartId ? { ...config, id: editingChartId } : c));
    } else {
      const id = `chart-${Date.now()}`;
      const newChart = { ...config, id };
      setCharts((prev) => [...prev, newChart]);
      const existing = [...cardPosRef.current, ...chartPosRef.current];
      const maxY = existing.reduce((m, p) => Math.max(m, p.y + p.h), 0);
      setChartPositions((prev) => [...prev, { id, x: 12, y: maxY + 12, w: 400, h: 280 }]);
    }
    setShowChartModal(false);
    setEditingChartId(null);
  }, [editingChartId]);

  const handleDeleteChart = useCallback((id: string) => {
    setCharts((prev) => prev.filter((c) => c.id !== id));
    setChartPositions((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const handleEditChart = useCallback((id: string) => {
    const chart = charts.find((c) => c.id === id);
    if (!chart) return;
    if (chart.sourceTableId && chart.sourceRows && chart.sourceCols) {
      setSelection({
        tableId: chart.sourceTableId,
        selectedRows: new Set(chart.sourceRows),
        selectedCols: new Set(chart.sourceCols),
      });
    }
    setEditingChartId(id);
    setShowChartModal(true);
  }, [charts]);

  /* ── PDF Export ── */
  const handleExportPdf = useCallback(async () => {
    if (!mapInstance) return;
    setExporting(true);
    try {
      const pdf = new MiniPDF();
      pdf.setHeader(reportName);

      // Page 1: Map screenshot
      const mapDataUrl = await captureMap(mapInstance);
      if (mapDataUrl) {
        const p = pdf.addPage('landscape');
        const { w: pw, h: ph } = pdf.getPageSize(p);
        const margin = 12;
        const topMargin = 44;
        const bottomMargin = 32;
        const availW = pw - margin * 2;
        const availH = ph - topMargin - bottomMargin;
        // Use full available area since we don't know original dimensions
        pdf.addImage(p, mapDataUrl, margin, topMargin, availW, availH);
        pdf.addText(p, t('report.map_overview'), pw / 2, topMargin - 4, { fontSize: 10, color: [100, 100, 100], align: 'center' });
      }

      // Page 2: Dashboard
      if (biContainerRef.current) {
        const dashDataUrl = await captureDashboard(biContainerRef.current);
        if (dashDataUrl) {
          const p = pdf.addPage('landscape');
          const { w: pw, h: ph } = pdf.getPageSize(p);
          const margin = 12;
          const topMargin = 44;
          const bottomMargin = 32;
          const availW = pw - margin * 2;
          const availH = ph - topMargin - bottomMargin;
          pdf.addImage(p, dashDataUrl, margin, topMargin, availW, availH);
          pdf.addText(p, t('report.dashboard'), pw / 2, topMargin - 4, { fontSize: 10, color: [100, 100, 100], align: 'center' });
        }
      }

      await pdf.download(`${reportName.replace(/\s+/g, '_')}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  }, [mapInstance, reportName, t]);

  const totalFeatures = results.reduce((s, r) => s + r.features.length, 0);

  /* ══════════════════════════════════════════════════════
     Fullscreen BI Mode
     ══════════════════════════════════════════════════════ */
  if (fullscreen) {
    const selTable = selection ? allTables.find((t) => t.id === selection.tableId) : null;

    return (
      <div className="fixed inset-0 z-[1100] bg-gn-primary flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 bg-gn-secondary border-b border-gn shrink-0">
          <div className="flex items-center gap-3">
            <Hexagon size={16} className="text-gn-blue" />
            {editingReportName ? (
              <input
                autoFocus
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                onBlur={() => setEditingReportName(false)}
                onKeyDown={(e) => e.key === 'Enter' && setEditingReportName(false)}
                className="bg-gn-tertiary text-gn-primary text-sm px-2 py-0.5 rounded border border-gn-blue outline-none w-64"
              />
            ) : (
              <span className="text-sm font-semibold text-gn-primary flex items-center gap-1 cursor-pointer hover:text-gn-blue"
                onClick={() => setEditingReportName(true)}>
                {reportName} <Edit3 size={12} className="text-gn-secondary" />
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={autoArrange} className="px-2 py-1 text-[10px] text-gn-primary bg-gn-tertiary rounded hover:bg-gn-hover flex items-center gap-1">
              <LayoutGrid size={12} /> {t('report.auto_arrange')}
            </button>
            <button onClick={handleExportPdf} disabled={exporting}
              className="px-2 py-1 text-[10px] text-gn-primary bg-gn-tertiary rounded hover:bg-gn-hover flex items-center gap-1 disabled:opacity-50">
              <FileDown size={12} /> {exporting ? t('report.exporting') : t('report.export_pdf')}
            </button>
            <button onClick={() => setFullscreen(false)}
              className="px-2 py-1 text-[10px] text-gn-primary bg-gn-tertiary rounded hover:bg-gn-hover flex items-center gap-1">
              <Minimize2 size={12} /> {t('report.exit')}
            </button>
          </div>
        </div>

        {/* Canvas area */}
        <div ref={biContainerRef} className="flex-1 overflow-auto relative bg-gn-primary" style={{ minHeight: 0 }}>
          {/* Table cards */}
          {allTables.map((table) => {
            const pos = cardPositions.find((p) => p.id === table.id);
            if (!pos) return null;
            return (
              <DraggableCard
                key={table.id}
                pos={pos}
                onMove={moveCard}
                onResize={resizeCard}
                containerRef={biContainerRef}
                title={table.layerName}
              >
                <SelectableTable
                  tableId={table.id}
                  keys={table.keys}
                  features={table.features}
                  layerName={table.layerName}
                  selection={selection?.tableId === table.id ? selection : null}
                  onSelectionChange={(sel) => setSelection(sel ? { ...sel, tableId: table.id } : null)}
                  onCreateChart={() => { setEditingChartId(null); setShowChartModal(true); }}
                />
              </DraggableCard>
            );
          })}

          {/* Chart cards */}
          {charts.map((ch) => {
            const pos = chartPositions.find((p) => p.id === ch.id);
            if (!pos) return null;
            return (
              <DraggableCard
                key={ch.id}
                pos={pos}
                onMove={moveCard}
                onResize={resizeCard}
                onRemove={handleDeleteChart}
                onEdit={handleEditChart}
                containerRef={biContainerRef}
                title={ch.title || ch.type}
              >
                <BIChart config={ch} />
              </DraggableCard>
            );
          })}
        </div>

        {/* Bottom bar: Add chart button */}
        {selection && selection.selectedCols.size > 0 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1200]">
            <button
              onClick={() => { setEditingChartId(null); setShowChartModal(true); }}
              className="px-4 py-2 bg-gn-blue text-gn-inverse text-xs font-medium rounded-lg shadow-lg hover:bg-gn-blue flex items-center gap-2"
            >
              <BarChart2 size={14} /> Add Chart
            </button>
          </div>
        )}

        {/* Chart modal */}
        {showChartModal && selTable && selection && (
          <ChartTypeModal
            selection={selection}
            keys={selTable.keys}
            features={selTable.features}
            onConfirm={handleChartConfirm}
            onCancel={() => { setShowChartModal(false); setEditingChartId(null); }}
            editingChart={editingChartId ? charts.find((c) => c.id === editingChartId) : null}
          />
        )}
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════
     Normal Panel Mode (bottom-anchored like CatchmentResultPanel)
     ══════════════════════════════════════════════════════ */
  return (
    <div
      className="fixed z-[900] flex flex-col bg-gn-primary backdrop-blur-sm border border-gn rounded-t-lg shadow-2xl"
      style={{
        left: rect.left,
        bottom: rect.bottom,
        width: rect.w,
        height: minimized ? 'auto' : rect.h,
      }}
    >
      {/* Resize handles */}
      {!minimized && (
        <>
          <div className="absolute top-0 left-0 right-0 h-1.5 cursor-n-resize hover:bg-gn-blue-light" onMouseDown={onResizeStart('top')} />
          <div className="absolute left-0 top-0 bottom-0 w-1.5 cursor-w-resize hover:bg-gn-blue-light" onMouseDown={onResizeStart('left')} />
          <div className="absolute right-0 top-0 bottom-0 w-1.5 cursor-e-resize hover:bg-gn-blue-light" onMouseDown={onResizeStart('right')} />
          <div className="absolute bottom-0 left-0 right-0 h-1.5 cursor-s-resize hover:bg-gn-blue-light" onMouseDown={onResizeStart('bottom')} />
          <div className="absolute top-0 left-0 w-3 h-3 cursor-nw-resize" onMouseDown={onResizeStart('top-left')} />
          <div className="absolute top-0 right-0 w-3 h-3 cursor-ne-resize" onMouseDown={onResizeStart('top-right')} />
        </>
      )}

      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-1.5 bg-gn-secondary rounded-t-lg cursor-move select-none border-b border-gn shrink-0"
        onMouseDown={onDragStart}
      >
        <div className="flex items-center gap-2 text-xs font-semibold text-gn-primary">
          <Hexagon size={13} className="text-gn-blue" />
          {t('tools.area_analysis')}
          {results.length > 0 && (
            <span className="text-[10px] text-gn-secondary font-normal">
              ({t('area.summary', { features: totalFeatures, layers: results.length })})
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {results.length > 0 && (
            <button onClick={enterFullscreen}
              className="p-1 text-gn-secondary hover:text-gn-blue hover:bg-gn-tertiary rounded" title={t('report.dashboard_mode')}>
              <Maximize2 size={13} />
            </button>
          )}
          <button onClick={() => setMinimized((v) => !v)}
            className="p-1 text-gn-secondary hover:text-gn-primary hover:bg-gn-tertiary rounded">
            {minimized ? <Maximize2 size={13} /> : <Minus size={13} />}
          </button>
          <button onClick={onClose} className="p-1 text-gn-secondary hover:text-gn-danger hover:bg-gn-tertiary rounded">
            <X size={13} />
          </button>
        </div>
      </div>

      {/* Body */}
      {!minimized && (
        <div className="flex-1 flex flex-col overflow-hidden p-3 pb-0 gap-2" style={{ minHeight: 0 }}>
          {/* Status */}
          {loading && (
            <div className="flex items-center gap-2 text-xs text-gn-blue shrink-0">
              <Loader2 size={14} className="animate-spin" />
              {t('area.querying')}
            </div>
          )}

          {!loading && !drawnFeature && (
            <div className="text-[11px] text-gn-warning bg-gn-warning-light border border-gn rounded px-2 py-1.5 flex items-center gap-1.5 shrink-0">
              <Hexagon size={12} />
              {t('area.draw_hint')}
            </div>
          )}

          {!loading && results.length === 0 && drawnFeature && (
            <div className="text-[11px] text-gn-secondary italic text-center py-3 shrink-0">
              {t('area.none')}
            </div>
          )}

          {/* Layer results — single loop preserves order */}
          {results.map((lr) => {
            const isExpanded = expandedLayer === lr.layerName;
            return (
              <div key={lr.layerName} className={cn(
                'rounded border border-gn',
                isExpanded ? 'flex flex-col min-h-0 flex-1' : 'shrink-0',
              )}>
                {/* Layer header */}
                <button
                  onClick={() => setExpandedLayer(isExpanded ? null : lr.layerName)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-gn-secondary hover:bg-gn-tertiary text-xs font-medium text-gn-primary shrink-0"
                >
                  <span className="flex items-center gap-1.5">
                    {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    {lr.layerName}
                  </span>
                  <span className="text-[10px] text-gn-secondary">{lr.features.length} features</span>
                </button>

                {isExpanded && <ExpandedLayerTable lr={lr} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
